import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Security } from '@okta/okta-react';
import { useNavigate } from 'react-router-dom';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { GetOktaSSOConfig } from './apis/auth';
import { buildOktaConfig } from './config/okta-config';
import type { OktaBootstrapConfig } from './config/okta-config';

interface OktaProviderProps {
  children: ReactNode;
}

interface OktaRuntimeValue {
  oktaAuth: OktaAuth | null;
  isReady: boolean;
  isEnabled: boolean;
  loginPath: string;
  config: OktaBootstrapConfig | null;
}

const defaultRuntimeValue: OktaRuntimeValue = {
  oktaAuth: null,
  isReady: false,
  isEnabled: false,
  loginPath: '/login',
  config: null,
};

export const OktaRuntimeContext =
  createContext<OktaRuntimeValue>(defaultRuntimeValue);

export const useOktaRuntime = (): OktaRuntimeValue =>
  useContext(OktaRuntimeContext);

const OktaProvider: React.FC<OktaProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [runtimeValue, setRuntimeValue] =
    useState<OktaRuntimeValue>(defaultRuntimeValue);

  useEffect(() => {
    let isActive = true;

    const bootstrap = async () => {
      try {
        const response = await GetOktaSSOConfig();
        const bootstrapConfig = response?.data || null;

        if (!isActive || !bootstrapConfig?.enabled) {
          if (isActive) {
            setRuntimeValue({
              oktaAuth: null,
              isReady: true,
              isEnabled: false,
              loginPath: bootstrapConfig?.login_path || '/login',
              config: bootstrapConfig,
            });
          }
          return;
        }

        const builtConfig = buildOktaConfig(bootstrapConfig);
        const oktaAuth = new OktaAuth(builtConfig.oidc);

        if (!isActive) return;

        setRuntimeValue({
          oktaAuth,
          isReady: true,
          isEnabled: true,
          loginPath: builtConfig.loginPath,
          config: bootstrapConfig,
        });
      } catch (error) {
        if (!isActive) return;
        console.error('Failed to bootstrap Okta configuration:', error);
        setRuntimeValue({
          oktaAuth: null,
          isReady: true,
          isEnabled: false,
          loginPath: '/login',
          config: null,
        });
      }
    };

    bootstrap();

    return () => {
      isActive = false;
    };
  }, []);

  const restoreOriginalUri = async (
    _oktaAuth: OktaAuth,
    originalUri?: string
  ) => {
    const path = originalUri
      ? toRelativeUrl(originalUri, window.location.origin)
      : '/';
    navigate(path, { replace: true });
  };

  const providerValue = useMemo(() => runtimeValue, [runtimeValue]);
  const content =
    runtimeValue.isEnabled && runtimeValue.oktaAuth ? (
      <Security
        oktaAuth={runtimeValue.oktaAuth}
        restoreOriginalUri={restoreOriginalUri}
      >
        {children}
      </Security>
    ) : (
      children
    );

  return (
    <OktaRuntimeContext.Provider value={providerValue}>
      {content}
    </OktaRuntimeContext.Provider>
  );
};

export const withOktaAuth = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  const WrappedComponent: React.FC<P> = (props: P) => (
    <OktaProvider>
      <Component {...props} />
    </OktaProvider>
  );

  WrappedComponent.displayName = `withOktaAuth(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
};

export default OktaProvider;
